import { withWidth } from '@material-ui/core'
import { terminateCurrentTour, updateTourStep } from 'actions/tourActions'
import { logAmplitudeEvent } from 'amplitude/amplitude'
import { authSelectors, removeTourBanner } from 'ducks/auth'
import { FujiTourType } from 'ducks/tour'
import { useShowTourBanner } from 'hooks/useShowTourBanner'
import { recordUserAction } from 'persistentContent/tours/tools/common'
import ConfirmModal from 'projectSections/sections/manage3/common/ConfirmModal'
import { useTranslate } from 'ra-core'
import { FC, useEffect, useState } from 'react'
import { useTourForceHide } from 'persistentContent/tours/tourOstools'
import { useDispatch, useSelector } from 'react-redux'
import FujiTour from '.'
import BannerDialog from './BannerDialog'
import { TOUR_CONFIG } from './util'

interface PropTypes {
  width: string
  tourType: FujiTourType
}

const TourBanner: FC<PropTypes> = ({ width, tourType }) => {
  const dispatch = useDispatch()
  const translate = useTranslate()
  const { tour } = useSelector((state: any) => state.tour)
  const { tourConditionsPass, showBanner } = useShowTourBanner(width, tourType)
  const [confirmDialog, showConfirm] = useState(false)
  const [endDialog, showEndDialog] = useState(false)
  const forceHideTours = useTourForceHide()
  const userId = useSelector(authSelectors.getCurrentUserId)
  const tourConfig = TOUR_CONFIG[tourType] || {}
  const { startBanner, confirmSkipText, finishBanner } = tourConfig
  useEffect(() => {
    return () => {
      handleHide() // Add this to prevent re opening the tour after user navigates to another page while the tour is still active
    }
  }, [])

  useEffect(() => {
    if (showBanner) logAmplitudeEvent('fuji_banner_shown', { tourType })
  }, [showBanner])

  const trackHidden = () => {
    if (userId) recordUserAction(userId, tourConfig.hidden)
    logAmplitudeEvent('fuji_banner_hidden', { tourType })
  }

  const handleHide = () => {
    dispatch(removeTourBanner(true, tourType))
    dispatch(terminateCurrentTour(tour))
    showConfirm(false)
  }

  const handleStart = () => {
    logAmplitudeEvent('fuji_tour_started', { tourType })
    dispatch(updateTourStep(tourType, tourConfig.startStep))
  }

  const handleDialogCloseClick = () => {
    logAmplitudeEvent('fuji_confirm_cancel_dialog_shown', { tourType })
    showConfirm(true) // Show the confirmation dialog when the close button is clicked
  }

  const handleEndTour = () => {
    if (userId) recordUserAction(userId, tourConfig.finishStep)
    dispatch(removeTourBanner(true, tourType))
    showEndDialog(false)
  }
  if (!tourConditionsPass || forceHideTours) return null
  return (
    <>
      <BannerDialog
        endDialog={false}
        open={showBanner}
        onClose={handleDialogCloseClick}
        headerText={startBanner.header}
        subText={startBanner.subText}
        actionLabel="Get Started"
        handleAction={handleStart}
        imgSrc={startBanner.imgSrc}
      />
      <ConfirmModal
        open={confirmDialog}
        setOpen={showConfirm}
        title={translate('Are you sure you want to skip?')}
        mainText={translate(confirmSkipText)}
        submitBtnLabel={translate('Skip Tour')}
        handleSubmit={() => {
          trackHidden()
          handleHide()
        }}
        submitBtnProps={{ variant: 'contained' }}
      />
      <BannerDialog
        open={endDialog}
        endDialog={endDialog}
        onClose={handleEndTour}
        headerText={finishBanner.header}
        subText={finishBanner.subText}
        actionLabel="Learn more"
        handleAction={() => {
          if (tourType === 'manage') {
            window.open(
              'https://support.opensolar.com/hc/en-us/articles/12455522489615-Getting-Started-Manage-Page',
              '_blank'
            )
          } else if (tourType === 'ada_chat') {
            window.open(
              'https://support.opensolar.com/hc/en-us/articles/13785730896911-Using-Ada-Chat-and-Voice',
              '_blank'
            )
          } else {
            window.open(
              ' https://support.opensolar.com/hc/en-us/articles/12455532120079-Getting-started-Projects-List',
              '_blank'
            )
          }
          handleEndTour()
        }}
      />
      <FujiTour
        tourConfig={tourConfig}
        toggleEndDialog={() => {
          showEndDialog(true)
        }}
      />
    </>
  )
}
export default withWidth()(TourBanner)
